import { Float, useGLTF } from '@react-three/drei';
import { GLTF } from 'three-stdlib';
import { GroupProps, useThree } from '@react-three/fiber';
import { useAppStore } from '../../context/use-app-store';
import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import gsap from 'gsap';

type CylinderType = GLTF & {
  nodes: {
    Cylinder1_1: THREE.Mesh;
  };
  materials: {
    Cylinder1_1: THREE.MeshStandardMaterial;
  };
};

type LayerType = GLTF & {
  nodes: {
    PM3D_Layers: THREE.Mesh;
  };
  materials: {
    PM3D_Layers: THREE.MeshStandardMaterial;
  };
};

type BigCylinderType = GLTF & {
  nodes: {
    Big_Cylinder1_1: THREE.Mesh;
  };
  materials: {
    Big_Cylinder1_1: THREE.MeshStandardMaterial;
  };
};

const ScaleFooter3D = () => {
  // Import GLTF Model
  const { nodes: CylinderNodes, materials: CylinderMaterials } = useGLTF(
    '/static/zeitgeist/2023/cylinder.gltf'
  ) as CylinderType;
  const { nodes: LayerNodes, materials: LayerMaterials } = useGLTF(
    '/static/zeitgeist/2023/layers.gltf'
  ) as LayerType;
  const { nodes: BigCylinderNodes, materials: BigCylinderMaterials } = useGLTF(
    '/static/zeitgeist/2023/big-cylinder.gltf'
  ) as BigCylinderType;

  // Refs
  const modelRef = useRef<GroupProps>(null!);

  // Store
  const timelineCompleted = useAppStore(({ timelineCompleted }) => timelineCompleted);

  // Get aspect ratio
  const aspect = useThree(state => state.viewport.aspect);
  const width = useThree(state => state.viewport.width);

  // Material Config
  const configMaterial = {
    anisotropy: 0.1,
    attenuationColor: '#ffffff',
    attenuationDistance: 0.5,
    backside: true,
    chromaticAberration: 1,
    distortion: 0.6,
    distortionScale: 0.5,
    ior: 1.2,
    meshPhysicalMaterial: false,
    resolution: 2048,
    roughness: 1,
    samples: 5,
    temporalDistortion: 1,
    thickness: 4,
    transmission: 1,
    transmissionSampler: false
  };

  // Entrance Animation
  useEffect(() => {
    if (!modelRef.current || !timelineCompleted || !aspect) return;
    const tl = gsap.timeline({
      paused: true,
      defaults: {
        ease: 'power3.out',
        duration: 0.7
      }
    });
    tl.fromTo(
      modelRef.current.scale,
      { x: 0, y: 0, z: 0 },
      {
        x: 0.005 * aspect,
        y: 0.005 * aspect,
        z: 0.005 * aspect
      }
    );
    tl.from(modelRef.current.rotation, { x: 0, y: 0, z: 0 }, '<');

    tl.play();

    return () => {
      tl.kill();
    };
  }, [timelineCompleted]);

  return (
    // @ts-ignore
    <group
      dispose={null}
      position={[0, 0, 0]}
      ref={modelRef}
      rotation={[0, 0, 0]}
      scale={0.005 * aspect}
      visible={timelineCompleted}
    >
      <ambientLight intensity={1} />
      {/* @ts-ignore */}
      <Float speed={1} rotationIntensity={2}>
        <mesh
          castShadow
          receiveShadow
          geometry={CylinderNodes.Cylinder1_1.geometry}
          material={CylinderMaterials.Cylinder1_1}
          position={[width * -16, 0, 0]}
          rotation={[1.45, 0.22, -0.29]}
        />
      </Float>
      {/* @ts-ignore */}
      <Float speed={1.5}>
        <mesh
          castShadow
          receiveShadow
          geometry={LayerNodes.PM3D_Layers.geometry}
          material={LayerMaterials.PM3D_Layers}
          position={[width * 35, 0, 0]}
          rotation={[-1.87, -3.33, -2.4]}
          scale={0.9}
        />
      </Float>
      {/* @ts-ignore */}
      <Float speed={2} floatIntensity={2}>
        <mesh
          castShadow
          receiveShadow
          geometry={BigCylinderNodes.Big_Cylinder1_1.geometry}
          material={BigCylinderMaterials.Big_Cylinder1_1}
          position={[width * -35, width * 20, 0]}
          rotation={[1.44, 0.01, -0.43]}
          scale={0.9}
        />
      </Float>
    </group>
  );
};

export default ScaleFooter3D;
